import React, { useEffect, useRef, useState } from 'react';
import L, { map } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.vectorgrid'; // Ensure this is installed: npm install leaflet.vectorgrid
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import './Map.css';
import * as d3 from 'd3-scale';
import { interpolateViridis } from 'd3-scale-chromatic';


    


const Map = () => {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const drawnItems = useRef(new L.FeatureGroup());
  const vectorLayerRef = useRef(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [selectedCity, setSelectedCity] = useState('');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  // useRef to hold the latest selection mode value for our event listener
  const isSelectionModeRef = useRef(isSelectionMode);
  const markerRef = useRef(null); // To store the search marker
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const selectionHistoryRef = useRef([]); // stack of { features, count, layer }
  const [canUndo, setCanUndo] = useState(false);
  const currentFeaturesRef = useRef([]);

  // for point selection
  const [isPointSelectionMode, setIsPointSelectionMode] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showRadiusInput, setShowRadiusInput] = useState(false);
  const [radius, setRadius] = useState('');
  const [radiusWarning, setRadiusWarning] = useState('');
  const pointLayer = useRef(null);
  const circleLayer = useRef(null);
  const isochroneActiveRef = useRef(false);
  const lastRadiusRef = useRef(null);
  const modeRef = useRef("truck");
  const customHostRef = useRef("");

  const vectorGridLayerRef = useRef(null)

  // for count of selected grid
  const [selectedGridCount, setSelectedGridCount] = useState(0);

  /*/ Duration threshold state
  const [durationThresholds, setDurationThresholds] = useState([120, 300, 600]); // default: 2, 5, 10 mins in seconds
  const [selectedOption, setSelectedOption] = useState('small');
  const [customMins, setCustomMins] = useState(["2", "5", "10"]); // input in minutes*/

  // Loading state for isochrone generation
  const [isLoadingIsochrone, setIsLoadingIsochrone] = useState(false);
  const [isochroneDone, setIsochroneDone] = useState(false);

  const durationsMapRef = useRef({});
  const distancesMapRef = useRef({});
  const [mode, setMode] = useState("truck"); // default mode
  const [customHost, setCustomHost] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  



  const setZoomLevel = () => {
    if (mapInstance.current) {
      mapInstance.current.setZoom(14);
      
      setTimeout(() => {
        if (mapInstance.current) {
          mapInstance.current.dragging.enable();
          mapInstance.current.touchZoom.enable();
          mapInstance.current.scrollWheelZoom.enable();
          mapInstance.current.doubleClickZoom.enable();
          mapInstance.current.boxZoom.enable();
          mapInstance.current.keyboard.enable();
          mapInstance.current.getContainer().style.cursor = "grab";
        
        if (vectorLayerRef.current && vectorLayerRef.current._tiles) {
          Object.values(vectorLayerRef.current._tiles).forEach((tile) => {
            if (tile && tile.el) {
              tile.el.style.pointerEvents = isSelectionMode ? 'auto' : 'none';
            }
          });
        }

        mapInstance.current.invalidateSize();
        console.log("Dragging Enabled:", mapInstance.current.dragging.enabled());
      }
    }, 500);
    }

  };

  // Update ref and toggle dragging/zooming based on selection mode
  useEffect(() => {
    isSelectionModeRef.current = isSelectionMode;
    if (mapInstance.current) {
      if (isSelectionMode) {
        mapInstance.current.dragging.disable();
        mapInstance.current.scrollWheelZoom.disable();
        mapInstance.current.getContainer().style.cursor = 'default';
      } else {
        mapInstance.current.dragging.enable();
        mapInstance.current.scrollWheelZoom.enable();
        mapInstance.current.getContainer().style.cursor = 'grab';
      }
    }
  }, [isSelectionMode]);

  useEffect(() => {
    isSelectionModeRef.current = isPointSelectionMode;
    if (mapInstance.current) {
      if (isPointSelectionMode) {
        mapInstance.current.dragging.disable();
        mapInstance.current.scrollWheelZoom.disable();
        mapInstance.current.getContainer().style.cursor = 'default';
      } else {
        mapInstance.current.dragging.enable();
        mapInstance.current.scrollWheelZoom.enable();
        mapInstance.current.getContainer().style.cursor = 'grab';
      }
    }
  }, [isPointSelectionMode]);




  // This effect updates pointer events on vector grid tiles after zoom events (and on mode change)
  useEffect(() => {
    const updateVectorTilePointerEvents = () => {
      if (vectorLayerRef.current && vectorLayerRef.current._container) {
        vectorLayerRef.current._container.style.pointerEvents = isSelectionMode ? 'auto' : 'none';
        //console.log('Updated vector grid pointer-events:', vectorLayerRef.current._container.style.pointerEvents);
      }
    };
    
    
    if (mapInstance.current) {
      // Update immediately
      updateVectorTilePointerEvents();
      // Update on every zoomend event
      mapInstance.current.on('zoomend', updateVectorTilePointerEvents);
      return () => {
        mapInstance.current.off('zoomend', updateVectorTilePointerEvents);
      };
    }
  }, [isSelectionMode]);

   
  // List of major cities in Germany with their coordinates
  const cities = [
    { name: 'Berlin', lat: 52.52, lng: 13.405 },
    { name: 'Munich', lat: 48.1351, lng: 11.582 },
    { name: 'Hamburg', lat: 53.5511, lng: 9.9937 },
    { name: 'Frankfurt', lat: 50.1109, lng: 8.6821 },
    { name: 'Cologne', lat: 50.9375, lng: 6.9603 },
    { name: 'Stuttgart', lat: 48.7758, lng: 9.1829 },
    { name: 'Düsseldorf', lat: 51.2277, lng: 6.7735 },
    { name: 'Leipzig', lat: 51.3397, lng: 12.3731 },
    { name: 'Dresden', lat: 51.0504, lng: 13.7373 },
    { name: 'Bremen', lat: 53.0793, lng: 8.8017 },
    { name: 'Hannover', lat: 52.3759, lng: 9.7320 },
    { name: 'Nuremberg', lat: 49.4520, lng: 11.0768 },
    { name:  'Wuppertal', lat: 51.2640, lng: 7.1783 },
  ];

  useEffect(() => {
    // Initialize the map only once
    if (!mapInstance.current && mapRef.current) {
      mapInstance.current = L.map(mapRef.current).setView([51.2640, 7.1783], 13);

      // Add base layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(mapInstance.current);

      // Initialize the vector grid layer (adjust the URL and layer name as needed)
      vectorLayerRef.current = L.vectorGrid.protobuf(
        'https://geogridrouter.de/data/data_6_14zoom/{z}/{x}/{y}.pbf',
        {
          rendererFactory: L.canvas.tile,
          interactive: true, // Make features clickable/interactable
          vectorTileLayerStyles: {
            // Replace 'output_transformed' with your actual layer name if different
            cleaned: {
              weight: 0.4,
              color: '#ff0000', //red color for Orange color use this  #ffa500
              opacity: 0.5
            }
          }
        }
      ).addTo(mapInstance.current);
      
      
      
      // click event listener to the vector grid layer
      //vectorLayerRef.current.on('click', function (e) {
      //  console.log('Vector grid feature clicked:', e.layer.properties);
      //});
      

      // Add Leaflet Draw controls for rectangle drawing (only rectangle is enabled)
      const drawControl = new L.Control.Draw({
        draw: {
          rectangle: true,
          polygon: true,
          circle: {
            showRadius: true,
            metric: true,
            feet: false
          },
          marker: false,
          polyline: false
        },
        edit: {
          featureGroup: drawnItems.current,
          remove: false,
          edit: false
        }
      });
      mapInstance.current.addControl(drawControl);
      mapInstance.current.addLayer(drawnItems.current);

      // Add hover tooltip for Draw Control
      document.querySelector('.leaflet-draw-toolbar').setAttribute('title', 'Click to draw a rectangle');

      // Add hover effect to draw control buttons
      const drawButtons = document.querySelectorAll('.leaflet-draw-toolbar a');
      drawButtons.forEach((button) => {
        button.addEventListener('mouseenter', () => {
          button.style.backgroundColor = '#ffcc00'; // Highlight on hover
        });
        button.addEventListener('mouseleave', () => {
          button.style.backgroundColor = ''; // Reset when not hovered
        });
      });

      // Override L.DomEvent.fakeStop if not present (for compatibility)
      if (!L.DomEvent.fakeStop) {
        L.DomEvent.fakeStop = function (e) {
          L.DomEvent.stopPropagation(e);
        };
      }

      // When a rectangle is drawn, get its bounds and select features within those bounds
      mapInstance.current.on(L.Draw.Event.CREATED, (event) => {

        // Check current selection mode using our ref
        if (!isSelectionModeRef.current) {
          // If not in selection mode, do nothing (or optionally remove the drawn layer)
          return;
        }

        const layer = event.layer;
        drawnItems.current.addLayer(layer);

        // Snapshot current selection for undo before overwriting it
        const snapshotFeatures = [...currentFeaturesRef.current];
        const snapshotCount = snapshotFeatures.length;

        const bounds = layer.getBounds();
        const minLng = bounds.getWest();
        const minLat = bounds.getSouth();
        const maxLng = bounds.getEast();
        const maxLat = bounds.getNorth();

        const queryUrl = `https://geogridrouter.de/osrm/5004/bbox?minLng=${minLng}&minLat=${minLat}&maxLng=${maxLng}&maxLat=${maxLat}`;

        fetch(queryUrl)
          .then((response) => response.json())
          .then((data) => {
            // Push snapshot to undo stack after successful fetch
            selectionHistoryRef.current.push({ features: snapshotFeatures, count: snapshotCount, layer });
            setCanUndo(true);

            if (Array.isArray(data)) {
              setSelectedFeatures(data);
              setSelectedGridCount(data.length);
            } else if (data && data.features) {
              setSelectedFeatures(data.features);
              setSelectedGridCount(data.features.length);
            } else {
              setSelectedFeatures([]);
              setSelectedGridCount(0);
            }
          })
          .catch((err) => console.error('Error fetching features:', err));
          setSelectedGridCount(0); // Reset count
      });

      mapInstance.current.dragging.enable();
      mapInstance.current.scrollWheelZoom.enable();
      mapInstance.current.getContainer().style.cursor = 'grab';

      
    }

    // Clean up the map on unmount
    return () => {
      if (mapInstance.current) {
        mapInstance.current.remove();
      }
    };
  }, []);

  // Keep ref in sync so draw event handlers (mounted once) see current features
  useEffect(() => { currentFeaturesRef.current = selectedFeatures; }, [selectedFeatures]);

  // Close search dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest('.search-wrap')) setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Function to search for a location — shows dropdown of up to 5 results
  const searchLocation = async () => {
    if (!searchQuery) return;
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(searchQuery)}`
      );
      const data = await response.json();
      if (data.length === 0) { alert('Location not found'); return; }
      setSearchResults(data);
      setShowSearchDropdown(true);
    } catch (error) {
      console.error('Error fetching location:', error);
    }
  };

  const selectSearchResult = ({ lat, lon, display_name }) => {
    if (markerRef.current) mapInstance.current.removeLayer(markerRef.current);
    markerRef.current = L.marker([lat, lon])
      .addTo(mapInstance.current)
      .bindPopup(`<b>${display_name}</b>`)
      .openPopup();
    mapInstance.current.setView([lat, lon], 12);
    setShowSearchDropdown(false);
    setSearchResults([]);
  };
  
  // Function to format coordinates for the API 
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /*const formatCoordinatesForAPI = (data) => {
    return data.map(({ x, y}) => `${x},${y}`).join(';');
  };*/
  const handleModeChange = (e) => {
    const newMode = e.target.value;
    modeRef.current = newMode; // update ref immediately so fetchIsochroneData uses new mode
    setMode(newMode);
    if (isochroneActiveRef.current && lastRadiusRef.current && selectedPoint) {
      fetchAndGenerateIsochrone(lastRadiusRef.current);
    }
  };

  const modePorts = {
    car: 5002,
    truck: 5003,
    bicycle: 5005,
  };

  const handleSetCustomHost = (e) => {
    if (e.key === 'Enter') {
      customHostRef.current = e.target.value;
      setCustomHost(e.target.value);
    }
  };

  const getBaseUrl = (mode, customHost) => {
    if (mode === "custom" && customHost) {
      console.log("Using custom host:", customHost);
      return `https://${customHost}/table/v1/driving`;
    }

    const port = modePorts[mode] || modePorts.truck; // default to truck
    return `https://geogridrouter.de/osrm/${port}/table/v1/driving`;
  };

  const [tileProgress, setTileProgress] = useState('');
  const matrixAbortRef = useRef(null);

  const cancelMatrixDownload = () => {
    if (matrixAbortRef.current) {
      matrixAbortRef.current.abort();
      matrixAbortRef.current = null;
    }
  };

  const fetchFullDistanceMatrixCSV = async () => {
    if (selectedFeatures.length < 1) {
      alert("Please select at least one point.");
      return;
    }

    const controller = new AbortController();
    matrixAbortRef.current = controller;

    setIsLoading(true);
    setIsReady(false);
    setTileProgress('');
    setErrorMessage('');

    const ids    = selectedFeatures.map(({ id }) => id);
    const coords = selectedFeatures.map(({ x, y }) => [x, y]);
    const N = ids.length;

    // Each tile has at most B sources + B destinations → max 2B coords in the URL
    // 50 × 17 chars × 2 = ~1700 chars, well within nginx's 8 KB request-line limit.
    const B = 50;

    const distMatrix = Array.from({ length: N }, () => new Array(N).fill(null));
    const durMatrix  = Array.from({ length: N }, () => new Array(N).fill(null));

    // Build the list of all tiles up-front so we can fire them all in parallel.
    const tiles = [];
    for (let r = 0; r < N; r += B) {
      for (let c = 0; c < N; c += B) tiles.push({ r, c });
    }
    let done = 0;

    try {
      await Promise.all(tiles.map(async ({ r, c }) => {
        const rEnd = Math.min(r + B, N);
        const cEnd = Math.min(c + B, N);
        const rLen = rEnd - r;
        const cLen = cEnd - c;

        const rSlice = coords.slice(r, rEnd);
        const cSlice = coords.slice(c, cEnd);

        let tileCoords, srcIdx, dstIdx;
        if (r === c) {
          tileCoords = rSlice;
          srcIdx = rSlice.map((_, i) => i).join(';');
          dstIdx = srcIdx;
        } else {
          tileCoords = [...rSlice, ...cSlice];
          srcIdx = rSlice.map((_, i) => i).join(';');
          dstIdx = cSlice.map((_, i) => rLen + i).join(';');
        }

        const coordStr = tileCoords.map(([x, y]) => `${x},${y}`).join(';');
        const baseUrl  = getBaseUrl(modeRef.current, customHostRef.current);
        const url = `${baseUrl}/${coordStr}?sources=${srcIdx}&destinations=${dstIdx}&annotations=distance,duration`;

        const res = await fetch(url, { signal: controller.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status} on tile (row ${r}, col ${c})`);
        const data = await res.json();

        for (let ri = 0; ri < rLen; ri++) {
          for (let ci = 0; ci < cLen; ci++) {
            distMatrix[r + ri][c + ci] = data.distances?.[ri]?.[ci] ?? null;
            durMatrix[r + ri][c + ci]  = data.durations?.[ri]?.[ci] ?? null;
          }
        }

        done++;
        setTileProgress(`${Math.round((done / tiles.length) * 100)}%`);
      }));

      // Build CSV
      const lines = ['start_id,dest_id,distance_m,duration_s\n'];
      for (let i = 0; i < N; i++) {
        for (let j = 0; j < N; j++) {
          lines.push(`${ids[i]},${ids[j]},${distMatrix[i][j] ?? ''},${durMatrix[i][j] ?? ''}\n`);
        }
      }

      const blob = new Blob(lines, { type: 'text/csv;charset=utf-8;' });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = 'distance_matrix.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);

      setIsReady(true);
      setTileProgress('');
    } catch (err) {
      if (err.name === 'AbortError') {
        setTileProgress('');
        // cancelled cleanly — no error banner
      } else {
        console.error('Matrix fetch failed:', err);
        setErrorMessage(`Distance matrix download failed: ${err.message}`);
        setTileProgress('');
      }
    } finally {
      matrixAbortRef.current = null;
      setIsLoading(false);
    }
  };



  


  const undoLastSelection = () => {
    const history = selectionHistoryRef.current;
    if (history.length === 0) return;
    const { features, count, layer } = history.pop();
    drawnItems.current.removeLayer(layer);
    setSelectedFeatures(features);
    setSelectedGridCount(count);
    if (history.length === 0) setCanUndo(false);
  };

  // Clear the drawn selection and the selected features
  const clearSelection = () => {
    selectionHistoryRef.current = [];
    setCanUndo(false);
    drawnItems.current.clearLayers();
    setSelectedFeatures([]);
    setSelectedGridCount(0);
    setIsReady(false); // Reset ready state
    setSelectedPoint(null);
    //setRadius('');
    if (pointLayer.current) {
      mapInstance.current.removeLayer(pointLayer.current);
      pointLayer.current = null;
    }
    if (circleLayer.current) {
      mapInstance.current.removeLayer(circleLayer.current);
      circleLayer.current = null;
    }
    if (vectorGridLayerRef.current) {
          mapInstance.current.removeLayer(vectorGridLayerRef.current);
          vectorGridLayerRef.current = null;
        }
    if (legendControl) {
          mapInstance.current.removeControl(legendControl);
          legendControl = null;
        }
    const existing = document.querySelector(".legend");
    if (existing && existing.parentNode) {
          existing.parentNode.removeChild(existing);
        }
    isochroneActiveRef.current = false;
    setIsochroneDone(false);
  };

  

  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    if (!isSelectionMode) setIsPointSelectionMode(false); // Disable Point Selection Mode when enabling Selection Mode
  };
  
  const togglePointSelectionMode = () => {
    setIsPointSelectionMode(!isPointSelectionMode);
    if (!isPointSelectionMode) setIsSelectionMode(false); // Disable Selection Mode when enabling Point Selection Mode
  };
  
  // Effect to handle point selection on the map
  useEffect(() => {
    if (mapInstance.current) {
      const handleMapClick = (e) => {
        if (!isPointSelectionMode) return; // Only run if Point Selection Mode is active
        const { lat, lng } = e.latlng;
        setSelectedPoint({ lat, lng });
        setRadius('');
  
        // Remove existing marker if present
        if (pointLayer.current) {
          mapInstance.current.removeLayer(pointLayer.current);
        }
  
        // Add a marker for the selected point
        pointLayer.current = L.circleMarker([lat, lng], {
          radius: 2,         // Small dot size
          color: '#ff0000',  // Red border
          fillColor: '#ff0000', 
          fillOpacity: 1
        }).addTo(mapInstance.current);

        setShowRadiusInput(true);
      };
      
      
      mapInstance.current.on('click', handleMapClick);
  
      return () => {
        mapInstance.current.off('click', handleMapClick);
      };
    }
  }, [isPointSelectionMode]); // Runs when mode changes
  
  const fetchAndGenerateIsochrone = async (radiusVal) => {
    if (!selectedPoint) return;
    const r = Number(radiusVal);
    if (!r || r <= 0) return;

    const { lat, lng } = selectedPoint;
    const queryUrl = `https://geogridrouter.de/osrm/5004/nearby?lng=${lng}&lat=${lat}&radius=${r}`;

    let features;
    try {
      const res = await fetch(queryUrl);
      const data = await res.json();
      features = Array.isArray(data) ? data : (data?.features || []);
      setSelectedFeatures(features);
      setSelectedGridCount(features.length);
    } catch (err) {
      console.error('Error fetching features:', err);
      return;
    }

    // Draw temporary circle (removed once isochrone renders)
    if (circleLayer.current) mapInstance.current.removeLayer(circleLayer.current);
    circleLayer.current = L.circle([lat, lng], {
      color: 'blue',
      fillColor: '#add8e6',
      fillOpacity: 0.4,
      radius: r,
    }).addTo(mapInstance.current);

    lastRadiusRef.current = r;

    if (features.length === 0) return;

    await generateIsochroneGrids(features, { lat, lng });
  };


  const handleCityChange = (event) => {
    const cityName = event.target.value;
    setSelectedCity(cityName);

    const city = cities.find((c) => c.name === cityName);
    if (city && mapInstance.current) {
      mapInstance.current.setView([city.lat, city.lng], 12); // Center map on city
    }
    if (vectorLayerRef.current && vectorLayerRef.current._tiles) {
          Object.values(vectorLayerRef.current._tiles).forEach((tile) => {
            if (tile && tile.el) {
              tile.el.style.pointerEvents = isSelectionMode ? 'auto' : 'none';
            }
          });
        }
  };

  useEffect(() => {
    setIsSelectionMode(false); // Ensures the effect runs at least once
  }, []);



  const generateIsochroneGrids = async (featuresArg, pointArg) => {
  const featuresToUse = featuresArg || selectedFeatures;
  const pointToUse = pointArg || selectedPoint;

  if (!pointToUse || featuresToUse.length === 0) {
    alert("Please select a point and ensure grids are loaded.");
    return;
  }
  if (circleLayer.current) {
    mapInstance.current.removeLayer(circleLayer.current);
    circleLayer.current = null;
  }

  setIsLoadingIsochrone(true);
  isochroneActiveRef.current = true;

  const source = { lng: pointToUse.lng, lat: pointToUse.lat };
  const destinations = featuresToUse.map(feature => ({ lng: feature.x, lat: feature.y }));

  const { durations, distances } = await fetchIsochroneData(source, destinations);

  const validDurations = durations.filter((d) => d != null);
  const maxDuration = Math.max(...validDurations);

  const viridisScale = d3.scaleSequential()
    .domain([0, maxDuration])
    .interpolator(interpolateViridis);

  const getColorByDuration = (duration) => viridisScale(maxDuration - duration);


  const durationMap = {};
  const selectedIdSet = new Set();
  const coloredIds = new Set();
  durationsMapRef.current = {};
  distancesMapRef.current = {};

  featuresToUse.forEach((feature, idx) => {
    durationsMapRef.current[feature.id] = durations[idx];
    distancesMapRef.current[feature.id] = distances[idx];
  });

  featuresToUse.forEach((feature, idx) => {
    durationMap[feature.id] = { duration: durations[idx], census: feature.census_data };
    selectedIdSet.add(feature.id);
  });

  if (vectorGridLayerRef.current) {
    mapInstance.current.removeLayer(vectorGridLayerRef.current);
  }

  vectorGridLayerRef.current = L.vectorGrid.protobuf('https://geogridrouter.de/data/data_6_14zoom/{z}/{x}/{y}.pbf', {
    interactive: true,
    getFeatureId: (feat) => feat.properties.id,
    vectorTileLayerStyles: {
      'cleaned': (properties, zoom) => {
        const data = durationMap[properties.id];
        if (data && data.duration !== undefined) {
          coloredIds.add(properties.id);
          return {
            fillColor: getColorByDuration(data.duration),
            fillOpacity: 0.6,
            color: 'transparent',
            weight: 0,
            fill: true,
          };
        } else {
          return {
            fill: false,
            color: 'transparent',
            weight: 0,
          };
        }
      }
    },
  }).addTo(mapInstance.current);
  vectorGridLayerRef.current.redraw();

  // Hover tooltip
  let hoverTooltip = null;

  vectorGridLayerRef.current.on('mouseover', (e) => {
    if (!e || !e.latlng || !e.layer || !e.layer.properties) return;

    const { id } = e.layer.properties;
    const data = durationMap[id];

    if (!data) return;

    const duration = Math.round(data.duration);
    const census = data.census ?? 'N/A';

    if (hoverTooltip) {
      mapInstance.current.removeLayer(hoverTooltip);
      hoverTooltip = null;
    }

    hoverTooltip = L.tooltip({
      sticky: true,
      direction: 'top',
      className: 'grid-hover-tooltip',
    })
      .setLatLng(e.latlng)
      .setContent(`
        <strong>Grid:</strong> ${id || 'Unknown'}<br>
        <strong>Duration:</strong> ${duration}s<br>
        <strong>Census Data:</strong> ${census}
      `)
      .addTo(mapInstance.current);
  });

  vectorGridLayerRef.current.on('mouseout', () => {
    if (hoverTooltip) {
      mapInstance.current.removeLayer(hoverTooltip);
      hoverTooltip = null;
    }
  });

  // ✅ Add dynamic legend
  if (legendControl) {
  mapInstance.current.removeControl(legendControl); // <-- FIXED
  legendControl = null;
}
addViridisLegend(mapInstance.current, maxDuration);

  setIsLoadingIsochrone(false);
  setIsochroneDone(true);
};

        

// Function to fetch isochrone data in chunks
const fetchIsochroneData = async (source, destinations, chunkSize = 100) => {
  const durationResults = [];
  const distanceResults = [];

  const chunkedRequests = [];

  for (let i = 0; i < destinations.length; i += chunkSize) {
    const chunk = destinations.slice(i, i + chunkSize);
    const coordString = chunk.map(coord => `${coord.lng},${coord.lat}`).join(';');

    const baseUrl = getBaseUrl(modeRef.current, customHostRef.current);
    const url = `${baseUrl}/${source.lng},${source.lat};${coordString}?sources=0&annotations=distance,duration`;

    function fetchWithTimeout(url, options = {}, timeout = 10000) { // 10s timeout
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeout);

      return fetch(url, { ...options, signal: controller.signal })
        .finally(() => clearTimeout(id));
    }

    
    // Push a promise to the list
    chunkedRequests.push(
      fetchWithTimeout(url, {}, 10000)
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
          return response.json();
        })
        .then((data) => {
          setErrorMessage(""); // Clear previous error on success
          const durations = data?.durations?.[0]?.slice(1) || [];
          const distances = data?.distances?.[0]?.slice(1) || [];
          return { durations, distances };
        })
        .catch((err) => {
          console.error("Error fetching chunk:", err.message || err);

          // 🔹 Show user-friendly message
          setErrorMessage(`Could not reach server at ${url}. Please check your IP:Port.`);

          setIsLoadingIsochrone(false);
          return { durations: [], distances: [] };
        })
    );

  }

  // Wait for all requests to finish
  const allResults = await Promise.all(chunkedRequests);

  // Combine the results in order
  allResults.forEach(({ durations, distances }) => {
    durationResults.push(...durations);
    distanceResults.push(...distances);
  });

  return { durations: durationResults, distances: distanceResults };
};


  
/*
  const handleDropdownChange = (value) => {
    setSelectedOption(value);
  
    const presets = {
      small: [120, 300, 600],
      medium: [300, 600, 1200],
      large: [600, 1200, 2400],
    };
  
    if (value === 'custom') {
      setDurationThresholds(customMins.map(min => min * 60));
    } else {
      setDurationThresholds(presets[value]);
    }
  };
  
  const handleCustomChange = (index, val) => {
    const newMins = [...customMins];
    newMins[index] = parseInt(val) || 0;
    setCustomMins(newMins);
    setDurationThresholds(newMins.map(min => min * 60));
  };*/
  
  
  let legendControl = null;

const addViridisLegend = (map, maxDuration) => {
  // 🔥 Extra cleanup: Remove any existing DOM element
  const existing = document.querySelector(".legend");
  if (existing && existing.parentNode) {
    existing.parentNode.removeChild(existing);
  }

  // Remove Leaflet control if it exists
  if (legendControl) {
    map.removeControl(legendControl);
    legendControl = null;
  }

  // Create new control
  legendControl = L.control({ position: "bottomright" });

  legendControl.onAdd = () => {
    const div = L.DomUtil.create("div", "legend leaflet-control");
    div.innerHTML += "<h4>Travel Time</h4>";

    const steps = 6;
    const stepSize = maxDuration / (steps - 1);

    for (let i = 0; i < steps; i++) {
      const val = maxDuration - i * stepSize;
      const color = interpolateViridis((maxDuration - val) / maxDuration);
      const minutes = Math.floor(val / 60);
      div.innerHTML += `
        <div style="display:flex; align-items:center;">
          <div style="width:20px; height:20px; background:${color}; margin-right:5px;"></div>
          <span>${minutes} min (${Math.round(val)} sec)</span>
        </div>
      `;
    }

    return div;
  };

  legendControl.addTo(map);
};




const downloadCSVForIsochrone = () => {
  if (!selectedFeatures || selectedFeatures.length === 0) {
    alert("No selected features to download.");
    return;
  }

  const headers = ["ID", "Cersus_data", "Duration (s)", "Distance (m)"];

  const selectedPointRow = [
    `Selected Point (${selectedPoint.lat.toFixed(4)} ${selectedPoint.lng.toFixed(4)})`, `n/a Radius= ${radius}`, 0, 0
  ]
  const rows = selectedFeatures.map((feature, idx) => {
    const duration = durationsMapRef.current[feature.id] ?? "";
    const distance = distancesMapRef.current[feature.id] ?? "";
    return [feature.id, feature.census_data, duration, distance];
  });

  const csvContent = [headers,selectedPointRow, ...rows]
    .map(row => row.join(","))
    .join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "selected_grids.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



  useEffect(() => {
  if (!mapInstance.current || !vectorLayerRef.current || !vectorLayerRef.current._tiles) return;

  const tiles = Object.values(vectorLayerRef.current._tiles);

  tiles.forEach((tile) => {
    if (tile && tile.el) {
      tile.el.style.pointerEvents = isSelectionMode ? 'auto' : 'none';
    }
    if (tile && tile.el) {
      tile.el.style.pointerEvents = isPointSelectionMode ? 'auto' : 'none';
    }
  });

}, [isSelectionMode], [isPointSelectionMode]);


  

  /*/ New effect: Force pointer events update after initial load
  useEffect(() => {
    if (mapInstance.current && !isSelectionMode) {
      // Wait 1 second after load to update pointer events on vector grid tiles
      setTimeout(() => {
        if (vectorLayerRef.current && vectorLayerRef.current._tiles) {
          Object.values(vectorLayerRef.current._tiles).forEach((tile) => {
            if (tile && tile.el) {
              tile.el.style.pointerEvents = 'none';
            }
          });
        }
      }, 100);
    }
  }, [isSelectionMode]); // Run once on initial load*/

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const geoStep = selectedGridCount > 0 ? 2 : isSelectionMode ? 1 : 0;
  const isoStep = isochroneDone ? 3 : isLoadingIsochrone ? 2 : selectedPoint ? 2 : isPointSelectionMode ? 1 : 0;

  return (
    <div className="app-layout">

      {/* ── Sidebar ── */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar--open' : ''}`}>

        <div className="sidebar-header">
          <span className="sidebar-brand">Controls</span>
          <button className="sidebar-close" onClick={() => setSidebarOpen(false)}>✕</button>
        </div>

        {/* Navigate */}
        <div className="sidebar-section">
          <p className="sidebar-section-title">Navigate</p>
          <div className="search-wrap sidebar-search">
            <input
              type="text"
              className="toolbar-input sidebar-input"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter') searchLocation(); }}
              placeholder="Search location..."
            />
            <button className="btn btn-secondary btn-sm" onClick={searchLocation}>Search</button>
            {showSearchDropdown && searchResults.length > 0 && (
              <div className="search-dropdown">
                {searchResults.map((r, i) => (
                  <button key={i} className="search-dropdown-item" onClick={() => { selectSearchResult(r); setSidebarOpen(false); }}>
                    {r.display_name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <select className="toolbar-select sidebar-full" value={selectedCity} onChange={handleCityChange}>
            <option value="">Jump to city...</option>
            {cities.map((city) => (
              <option key={city.name} value={city.name}>{city.name}</option>
            ))}
          </select>
        </div>

        <div className="sidebar-divider" />

        {/* Vehicle */}
        <div className="sidebar-section">
          <p className="sidebar-section-title">Vehicle</p>
          <div className="mode-pills">
            {[
              { value: 'car',     label: 'Car',     icon: '🚗' },
              { value: 'truck',   label: 'Truck',   icon: '🚛' },
              { value: 'bicycle', label: 'Bicycle', icon: '🚲' },
              { value: 'custom',  label: 'Custom',  icon: '⚙' },
            ].map(({ value, label, icon }) => (
              <button
                key={value}
                className={`mode-pill ${mode === value ? 'mode-pill--active' : ''}`}
                onClick={() => handleModeChange({ target: { value } })}
              >
                <span className="mode-pill-icon">{icon}</span>
                <span className="mode-pill-label">{label}</span>
              </button>
            ))}
          </div>
          {mode === 'custom' && (
            <input
              type="text"
              className="toolbar-input sidebar-full"
              placeholder="ip:port"
              value={customHost}
              onChange={(e) => setCustomHost(e.target.value)}
              onKeyDown={handleSetCustomHost}
              style={{ marginTop: '8px' }}
            />
          )}
        </div>

        <div className="sidebar-divider" />

        {/* Map actions */}
        <div className="sidebar-section">
          <p className="sidebar-section-title">Map</p>
          <div className="sidebar-actions">
            <button className="btn btn-ghost btn-sm" onClick={setZoomLevel}>⊕ Zoom 14</button>
            {canUndo && <button className="btn btn-ghost btn-sm" onClick={undoLastSelection}>↩ Undo</button>}
            <button className="btn btn-danger btn-sm" onClick={clearSelection}>✕ Clear</button>
          </div>
        </div>

        {errorMessage && (
          <div className="error-banner" style={{ margin: '0 12px 12px' }}>&#9888; {errorMessage}</div>
        )}

        <div className="sidebar-divider" />

        {/* Isochrone Panel */}
        <div className={`sidebar-panel ${isPointSelectionMode ? 'panel--active' : ''}`}>
          <div className="panel-header">
            <h3 className="panel-title">Isochrone Tools</h3>
            <span className={`panel-status ${isLoadingIsochrone ? 'ps-loading' : isochroneDone ? 'ps-ready' : isPointSelectionMode ? 'ps-active' : 'ps-idle'}`}>
              {isLoadingIsochrone ? 'Generating…' : isochroneDone ? 'Ready' : isPointSelectionMode ? 'Pick point…' : 'Idle'}
            </span>
          </div>
          <div className="step-flow">
            <div className={`step-item ${isoStep === 0 ? 'step-current' : 'step-done'}`}>
              <span className="step-dot">{isoStep > 0 ? '✓' : '1'}</span>
              <div className="step-body">
                <span className="step-title">Enable Point Mode</span>
                {isoStep === 0 && <span className="step-hint">Use the toggle below</span>}
              </div>
            </div>
            <div className="step-connector" />
            <div className={`step-item ${isoStep === 1 ? 'step-current' : isoStep > 1 ? 'step-done' : 'step-pending'}`}>
              <span className="step-dot">{isoStep > 1 ? '✓' : '2'}</span>
              <div className="step-body">
                <span className="step-title">Click map &amp; enter radius</span>
                {isoStep === 1 && <span className="step-hint">Click the map, max 10 km radius</span>}
              </div>
            </div>
            <div className="step-connector" />
            <div className={`step-item ${isoStep >= 2 ? (isochroneDone ? 'step-done' : 'step-current') : 'step-pending'}`}>
              <span className="step-dot">{isochroneDone ? '✓' : '3'}</span>
              <div className="step-body">
                <span className="step-title">Isochrone auto-generates</span>
                {isLoadingIsochrone && <span className="step-hint">Computing travel times…</span>}
                {isochroneDone && <span className="step-hint">Change vehicle to auto-update</span>}
              </div>
            </div>
          </div>
          <div className="panel-actions">
            <button className={`btn ${isPointSelectionMode ? 'btn-active' : 'btn-outline'}`} onClick={togglePointSelectionMode}>
              {isPointSelectionMode ? '✓ Point Mode ON' : 'Enable Point Mode'}
            </button>
            <button className="btn btn-primary" onClick={() => generateIsochroneGrids()} disabled={isLoadingIsochrone || !selectedPoint}>
              {isLoadingIsochrone ? <><span className="spinner" />Generating...</> : '↻ Re-generate'}
            </button>
            <button className="btn btn-ghost" onClick={downloadCSVForIsochrone} disabled={!isochroneDone}>Download CSV</button>
          </div>
        </div>

        <div className="sidebar-divider" />

        {/* GeoData Panel */}
        <div className={`sidebar-panel ${isSelectionMode ? 'panel--active' : ''}`}>
          <div className="panel-header">
            <h3 className="panel-title">GeoData Selection</h3>
            <span className={`panel-status ${isSelectionMode ? 'ps-active' : selectedGridCount > 0 ? 'ps-ready' : 'ps-idle'}`}>
              {isSelectionMode ? 'Selecting…' : selectedGridCount > 0 ? `${selectedGridCount} grids` : 'Idle'}
            </span>
          </div>
          <div className="step-flow">
            <div className={`step-item ${geoStep === 0 ? 'step-current' : 'step-done'}`}>
              <span className="step-dot">{geoStep > 0 ? '✓' : '1'}</span>
              <div className="step-body">
                <span className="step-title">Enable Selection Mode</span>
                {geoStep === 0 && <span className="step-hint">Use the toggle below</span>}
              </div>
            </div>
            <div className="step-connector" />
            <div className={`step-item ${geoStep === 1 ? 'step-current' : geoStep > 1 ? 'step-done' : 'step-pending'}`}>
              <span className="step-dot">{geoStep > 1 ? '✓' : '2'}</span>
              <div className="step-body">
                <span className="step-title">Draw shape on map</span>
                {geoStep === 1 && <span className="step-hint">Polygon, rectangle, or circle (top-left)</span>}
              </div>
            </div>
            <div className="step-connector" />
            <div className={`step-item ${geoStep === 2 ? 'step-current' : 'step-pending'}`}>
              <span className="step-dot">3</span>
              <div className="step-body">
                <span className="step-title">Download Distance Matrix</span>
                {geoStep === 2 && <span className="step-hint">Click the button below</span>}
              </div>
            </div>
          </div>
          <div className="panel-actions">
            <button className={`btn ${isSelectionMode ? 'btn-active' : 'btn-outline'}`} onClick={toggleSelectionMode}>
              {isSelectionMode ? '✓ Selection ON' : 'Enable Selection Mode'}
            </button>
            <button
              className={`btn download-btn ${isLoading ? 'loading' : ''} ${isReady ? 'ready' : ''}`}
              onClick={fetchFullDistanceMatrixCSV}
              disabled={isLoading || selectedGridCount === 0}
            >
              {isLoading ? <><span className="spinner" />Fetching… {tileProgress}</> : isReady ? '✓ Downloaded' : 'Download CSV'}
            </button>
            {isLoading && <button className="btn btn-cancel" onClick={cancelMatrixDownload}>✕ Cancel</button>}
          </div>
          <p className={`note ${selectedFeatures.length > 1500 ? 'warning' : ''}`}>
            {selectedFeatures.length > 1500 ? '⚠ Large selection — may take several minutes.' : 'Est: 500 grids ≈ 8 s · 1 000 ≈ 35 s · 2 000 ≈ 100 s'}
          </p>
        </div>

        <div style={{ height: '16px' }} />
      </aside>

      {/* Backdrop for tablet / mobile */}
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}

      {/* ── Map area ── */}
      <div className="map-area">
        <button
          className={`sidebar-toggle ${sidebarOpen ? 'sidebar-toggle--open' : ''}`}
          onClick={() => setSidebarOpen(s => !s)}
        >
          {sidebarOpen ? '✕' : '☰ Controls'}
        </button>
        <div id="map-container" ref={mapRef} />

      </div>{/* end map-area */}

      {/* Radius modal — overlays everything */}
      {showRadiusInput && (
        <div className="modal-overlay">
          <div className="modal-card">
            <h2 className="modal-title">Enter Radius</h2>
            <p className="modal-desc">Radius in metres around the selected point (max 10,000 m).</p>
            <input
              type="number"
              className="modal-input"
              autoFocus
              placeholder="e.g. 1500"
              value={radius}
              onChange={(e) => {
                const val = e.target.value;
                setRadius(val);
                setRadiusWarning(Number(val) > 10000 ? 'Radius cannot exceed 10,000 m. Please enter a smaller value.' : '');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const r = Number(radius);
                  if (r > 10000) { setRadiusWarning('Radius cannot exceed 10,000 m. Please enter a smaller value.'); return; }
                  setRadiusWarning('');
                  setShowRadiusInput(false);
                  fetchAndGenerateIsochrone(r);
                }
              }}
            />
            {radiusWarning && <p className="modal-warning">{radiusWarning}</p>}
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => { setShowRadiusInput(false); setRadiusWarning(''); }}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!!radiusWarning || !radius}
                onClick={() => {
                  const r = Number(radius);
                  if (r > 10000) { setRadiusWarning('Radius cannot exceed 10,000 m. Please enter a smaller value.'); return; }
                  setRadiusWarning('');
                  setShowRadiusInput(false);
                  fetchAndGenerateIsochrone(r);
                }}
              >
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Map;
